import { pinReplenishmentInstrument, type ChainStepRow, type InstrumentRow, type RequirementRow } from '@api/replenishment-engine';
import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';
import { Button, Group, Select, Stack, Text } from '@mantine/core';
import { formatCount } from '@utils/currency';
import { useCallback, useMemo, useState, type FC, type ReactElement } from 'react';

import ExplainWindow from '../ExplainWindow';
import { dayLabel, localDateKey, whenLabel } from './dates';
import { mergeChains } from './dips';
import { Lines, StatusBadge } from './tables';

const n = (v: number, maxDecimals = 1): string => formatCount(v, { maxDecimals });

type Props = {
    kind: 'MATERIALS' | 'TRANSFERS' | 'ORDERS';
    /** The dip's head row: the SKU, its route and facility. */
    row: RequirementRow;
    /** The parts whose chains the window lists: one for a part row, every part for a dip row. */
    parts: RequirementRow[];
    onClose: () => void;
    onApprove: (ids: string[]) => void;
    onSave?: (ids: string[]) => void;
    onChanged: () => void;
};

type Line = ChainStepRow & { part: number; instrument: InstrumentRow | null; key: string };

/**
 * The three cell drill-downs of a SKU row: the material sheet for its parts, their
 * transfers, their material orders. One component, three column sets, all approvable here.
 * A material line is per part (each part needs its own quantity); a transfer or an order
 * is listed once however many parts draw on it.
 */
const CellWindow: FC<Props> = ({ kind, row, parts, onClose, onApprove, onSave, onChanged }) => {
    const today = localDateKey();
    const [busy, setBusy] = useState<string | null>(null);
    const manyParts = parts.length > 1;
    const byId = useMemo(() => new Map(parts.flatMap((p) => p.instruments).map((i) => [i.id, i])), [parts]);
    const lines = useMemo<Line[]>(
        () =>
            mergeChains(parts)
                .filter((c) => (kind === 'MATERIALS' ? c.step === 'MATERIAL' : kind === 'TRANSFERS' ? c.step === 'TRANSFER' : c.step === 'MATERIAL' && c.instrumentId))
                .map((c, i) => ({ ...c, instrument: c.instrumentId ? (byId.get(c.instrumentId) ?? null) : null, key: `${c.step}|${c.part}|${c.itemId}|${c.vendorId ?? ''}|${c.fromSiteId ?? ''}|${i}` })),
        [parts, kind, byId],
    );

    const pick = useCallback(
        async (inst: InstrumentRow, vendorId: string): Promise<void> => {
            setBusy(inst.id);
            try {
                await pinReplenishmentInstrument(inst.id, { vendorId });
                onChanged();
            } finally {
                setBusy(null);
            }
        },
        [onChanged],
    );

    const columns = useMemo<DataSheetColumn<Line>[]>(() => {
        const material: DataSheetColumn<Line> = { key: 'material', header: 'Material', width: 240, sticky: true, render: (_v, l): ReactElement => <Lines lines={[{ text: l.itemName ?? l.itemId, strong: true }, { text: '', sub: `${l.unit ?? ''}${manyParts ? `${l.unit ? ' · ' : ''}part ${l.part}` : ''}` }]} /> };
        const status: DataSheetColumn<Line> = { key: 'status', header: 'Status', width: 110, render: (_v, l): ReactElement => (l.instrument ? <StatusBadge status={l.instrument.status} /> : <Text size="xs" c="dimmed">{l.note ?? 'on the floor'}</Text>) };
        if (kind === 'MATERIALS') {
            // short is what an order brings when an instrument carries it, else what nothing covers yet.
            const byOrder = (l: Line): number => (l.instrumentId ? l.short : 0);
            const uncovered = (l: Line): number => (l.instrumentId ? 0 : l.short);
            return [
                material,
                { key: 'need', header: 'Needed', width: 100, align: 'right', render: (_v, l): ReactElement => <Text size="sm">{n(l.qty)}</Text> },
                { key: 'floor', header: 'On floor', width: 100, align: 'right', render: (_v, l): ReactElement => <Text size="sm">{n(l.available)}</Text> },
                { key: 'movable', header: 'By transfer', width: 100, align: 'right', render: (_v, l): ReactElement => <Text size="sm" c={l.movable > 0 ? undefined : 'dimmed'}>{n(l.movable)}</Text> },
                { key: 'order', header: 'By order', width: 100, align: 'right', render: (_v, l): ReactElement => <Text size="sm" c={byOrder(l) > 0 ? undefined : 'dimmed'}>{n(byOrder(l))}</Text> },
                { key: 'short', header: 'Short', width: 100, align: 'right', render: (_v, l): ReactElement => <Text size="sm" c={uncovered(l) > 0 ? 'red' : 'dimmed'} fw={uncovered(l) > 0 ? 700 : 400}>{n(uncovered(l))}</Text> },
                { key: 'how', header: 'How it arrives', width: 240, render: (_v, l): ReactElement => <Lines lines={[{ text: l.short > 0 ? `${l.vendorName ?? 'no vendor mapped'} · ${l.leadDays ?? '—'}d` : (l.note ?? 'on the floor'), sub: l.short > 0 ? `place ${whenLabel(l.placeBy, today)} · lands ${dayLabel(l.lands)}` : '' }]} /> },
                status,
            ];
        }
        if (kind === 'TRANSFERS') {
            return [
                material,
                { key: 'qty', header: 'Move', width: 100, align: 'right', render: (_v, l): ReactElement => <Text size="sm" fw={700}>{n(l.qty)}</Text> },
                { key: 'route', header: 'From → to', width: 220, render: (_v, l): ReactElement => <Text size="sm">{`${l.fromSiteId} → ${l.toSiteId}`}</Text> },
                { key: 'dispatch', header: 'Dispatch by', width: 110, render: (_v, l): ReactElement => <Lines lines={[{ text: whenLabel(l.placeBy, today), late: Boolean(l.placeBy && l.placeBy <= today && l.status === 'PROPOSED') }]} /> },
                { key: 'lands', header: 'Lands', width: 110, render: (_v, l): ReactElement => <Text size="sm">{`${dayLabel(l.lands)} · ${l.leadDays ?? '—'}d`}</Text> },
                status,
            ];
        }
        return [
            material,
            { key: 'qty', header: 'Order', width: 100, align: 'right', render: (_v, l): ReactElement => <Text size="sm" fw={700}>{n(l.instrument?.qty ?? l.short)}</Text> },
            {
                key: 'vendor',
                header: 'Vendor',
                width: 260,
                render: (_v, l): ReactElement => {
                    const inst = l.instrument;
                    if (inst && inst.status === 'PROPOSED' && inst.vendorCandidates.length > 1) {
                        return (
                            <Select
                                size="xs"
                                value={inst.vendorId ?? null}
                                data={inst.vendorCandidates.map((c) => ({ value: c.vendorId, label: `${c.vendorName ?? c.vendorId} · ${c.leadDays}d` }))}
                                onChange={(v) => v && void pick(inst, v)}
                                disabled={busy === inst.id}
                                placeholder="pick a vendor"
                            />
                        );
                    }
                    return <Lines lines={[{ text: l.vendorName ?? 'no vendor mapped', late: !l.vendorName, sub: `${l.leadDays ?? '—'}d` }]} />;
                },
            },
            // Where the vendor delivers: the factory, or the hub its lane ends at (a transfer then relays it).
            { key: 'to', header: 'Delivered to', width: 200, render: (_v, l): ReactElement => <Lines lines={[{ text: l.toSiteId ?? '—', sub: l.toSiteId && l.toSiteId !== row.facilityId ? 'then relayed to the factory' : 'the factory' }]} /> },
            { key: 'placeBy', header: 'Place by', width: 110, render: (_v, l): ReactElement => <Lines lines={[{ text: whenLabel(l.placeBy, today), late: Boolean(l.placeBy && l.placeBy <= today && l.status === 'PROPOSED') }]} /> },
            { key: 'lands', header: 'Lands', width: 110, render: (_v, l): ReactElement => <Text size="sm">{dayLabel(l.lands)}</Text> },
            status,
        ];
    }, [kind, today, busy, pick, manyParts, row.facilityId]);

    const seen = new Set<string>();
    const open = lines.map((l) => l.instrument).filter((i): i is InstrumentRow => Boolean(i && i.status === 'PROPOSED' && (seen.has(i.id) ? false : (seen.add(i.id), true))));
    const title = kind === 'MATERIALS' ? `Material at ${row.facilityId ?? 'factory'}` : kind === 'TRANSFERS' ? 'Transfers' : 'RM / PM orders';
    const made = row.madeAsProductId ?? row.sku;
    const one = parts.length === 1 ? parts[0] : null;
    const subtitle =
        one && one.parts > 1
            ? `part ${one.part} of ${one.parts} · making ${n(one.qty, 0)} of ${one.neededAtLanding != null ? n(one.neededAtLanding, 0) : '—'} ${made} needed by ${dayLabel(one.landDate)}${one.startDate ? ` · starts ${dayLabel(one.startDate)}` : ''}`
            : `run of ${n(row.requiredQty, 0)} ${made}${parts.length > 1 ? ` in ${parts.length} parts` : ''}${parts[0]?.startDate ? ` starting ${dayLabel(parts[0].startDate)}` : ''}`;
    return (
        <ExplainWindow
            opened
            wide
            onClose={onClose}
            title={`${row.sku} · ${title}`}
            subtitle={subtitle}
            footer={
                open.length > 0 ? (
                    <Group justify="flex-end" p="sm">
                        {onSave && (
                            <Button size="xs" variant="subtle" color="grape" title="Keep these numbers as they stand, without creating any document" onClick={() => onSave(open.map((i) => i.id))}>
                                Save
                            </Button>
                        )}
                        <Button size="xs" onClick={() => onApprove(open.map((i) => i.id))}>
                            Approve {open.length} {open.length === 1 ? 'line' : 'lines'}
                        </Button>
                    </Group>
                ) : undefined
            }
        >
            <Stack gap="sm" p="md">
                <DataSheet<Line> data={lines} columns={columns} getRowKey={(l) => l.key} rowHeight={48} bare hideHeaderBar />
            </Stack>
        </ExplainWindow>
    );
};

export default CellWindow;
